import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';

const STORAGE_KEY = 'nubesmty_welcome_seen';

const KEYFRAMES = `
  @keyframes nMTY-float {
    0%, 100% { transform: translateY(0px); }
    50%       { transform: translateY(-9px); }
  }
  @keyframes nMTY-bounce-in {
    0%   { transform: translateY(32px) scale(0.82); opacity: 0; }
    55%  { transform: translateY(-7px) scale(1.06); opacity: 1; }
    75%  { transform: translateY(3px)  scale(0.97); }
    90%  { transform: translateY(-2px) scale(1.01); }
    100% { transform: translateY(0px)  scale(1);    }
  }
  .logo-bounce { animation: nMTY-bounce-in 0.7s cubic-bezier(0.34,1.56,0.64,1) both; }
  .logo-float  { animation: nMTY-float 3.2s ease-in-out infinite; }
`;

const AGE_KEY = 'nubes_age_verified_v1';

export default function WelcomePopup() {
  const [visible, setVisible] = useState(false);
  const [animIn, setAnimIn] = useState(false);
  const [logoAnim, setLogoAnim] = useState('');
  const navigate = useNavigate();

  const show = () => {
    setVisible(true);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      setAnimIn(true);
      setLogoAnim('logo-bounce');
    }));
  };

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) return; // ya visto, nunca mostrar

    // Caso A: edad ya verificada antes de cargar esta sesión → mostrar tras 800 ms
    if (localStorage.getItem(AGE_KEY)) {
      const t = setTimeout(show, 800);
      return () => clearTimeout(t);
    }

    // Caso B: esperamos a que el AgeGate sea confirmado en esta misma sesión
    const onAgeConfirmed = () => {
      if (localStorage.getItem(STORAGE_KEY)) return;
      const t = setTimeout(show, 1500);
      return () => clearTimeout(t); // no es posible limpiar desde aquí, pero es inofensivo
    };
    window.addEventListener('nubesmty:age-confirmed', onAgeConfirmed, { once: true });
    return () => window.removeEventListener('nubesmty:age-confirmed', onAgeConfirmed);
  }, []);

  // After bounce ends, switch to continuous float
  useEffect(() => {
    if (logoAnim === 'logo-bounce') {
      const t = setTimeout(() => setLogoAnim('logo-float'), 750);
      return () => clearTimeout(t);
    }
  }, [logoAnim]);

  const dismiss = () => {
    setAnimIn(false);
    setTimeout(() => {
      setVisible(false);
      localStorage.setItem(STORAGE_KEY, '1');
    }, 300);
  };

  const goRegister = () => {
    dismiss();
    setTimeout(() => navigate('/registro'), 320);
  };

  if (!visible) return null;

  return (
    <>
      <style>{KEYFRAMES}</style>
      <div
      data-testid="welcome-popup-overlay"
      className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
      onClick={(e) => e.target === e.currentTarget && dismiss()}
    >
      <div
        data-testid="welcome-popup"
        className="relative w-full max-w-md"
        style={{
          transform: animIn ? 'translateY(0) scale(1)' : 'translateY(24px) scale(0.96)',
          opacity: animIn ? 1 : 0,
          transition: 'transform 0.35s cubic-bezier(0.34,1.56,0.64,1), opacity 0.3s ease',
        }}
      >
        {/* Outer glow */}
        <div className="absolute -inset-px rounded-3xl bg-gradient-to-br from-orange-500/40 via-transparent to-orange-600/20 pointer-events-none" />

        <div className="relative bg-neutral-950 border border-orange-500/30 rounded-3xl overflow-hidden shadow-2xl shadow-orange-500/10">
          {/* Top accent bar */}
          <div className="h-1 w-full bg-gradient-to-r from-orange-500 via-orange-400 to-orange-600" />

          {/* Close button */}
          <button
            data-testid="welcome-popup-close"
            onClick={dismiss}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-all"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="px-7 pt-7 pb-8 flex flex-col items-center text-center">
            {/* Logo */}
            <img
              src="/brand/logo-nubesmty.png"
              alt="NubesMTY"
              className={`w-36 sm:w-44 h-auto object-contain mb-2 drop-shadow-lg ${logoAnim}`}
            />

            {/* Title */}
            <h2 className="text-2xl sm:text-3xl font-black text-white leading-tight tracking-tight">
              Bienvenido a{' '}
              <span className="text-orange-400">NubesMTY</span>
            </h2>

            {/* Subtitle */}
            <p className="mt-2 text-white/60 text-sm sm:text-base leading-snug max-w-xs">
              El mejor vape shop de Monterrey, ahora en tu puerta.
            </p>

            {/* Divider */}
            <div className="my-5 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

            {/* Message */}
            <p className="text-white/75 text-sm leading-relaxed max-w-xs">
              Regístrate y activa tu cupón de bienvenida para tu primer pedido
            </p>

            {/* Coupon code */}
            <div className="mt-4 px-6 py-3 rounded-2xl bg-orange-500/10 border border-orange-500/35 flex flex-col items-center gap-0.5">
              <span className="text-[10px] uppercase tracking-widest font-bold text-orange-400/70">tu código</span>
              <span
                data-testid="welcome-coupon-code"
                className="text-2xl sm:text-3xl font-black tracking-widest text-orange-400 font-mono"
              >
                BIENVENIDO10
              </span>
            </div>

            {/* Primary CTA */}
            <button
              data-testid="welcome-popup-register-btn"
              onClick={goRegister}
              className="mt-6 w-full h-12 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-black font-black text-[15px] shadow-lg shadow-orange-500/25 transition-all active:scale-[0.98]"
            >
              Registrarme y activar cupón
            </button>

            {/* Secondary CTA */}
            <button
              data-testid="welcome-popup-skip-btn"
              onClick={dismiss}
              className="mt-3 text-xs text-white/40 hover:text-white/70 transition-colors underline underline-offset-2"
            >
              Continuar sin registrarme
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
